/**
 * TrendPulse entry signals: a two-point horizontal stall immediately after a strong thrust.
 *
 * <h2>One pattern, one question</h2>
 *
 * This replaced a six-pattern classifier. The question is now only "is this an entry", and the
 * shape that answers it is three consecutive points on the curve: a decisive move into `P[t-1]`,
 * and then nothing — `P[t]` level with it. Momentum arrived and stopped. The direction of the call
 * is the opposite of the thrust that stalled: a fall that goes flat is a bullish entry.
 *
 * <h2>Scale is local, and that is the whole reason it works across eras</h2>
 *
 * `Range₃₀ = max(TP[t-29..t]) - min(TP[t-29..t])`, and both thresholds are fractions of it:
 * `ε_flat` at 0.75% says what counts as level, `Δ_slope` at 5% says what counts as a thrust. A
 * figure taken over the whole loaded history cannot do this — TrendPulse spans 11,332 points over
 * 1801–2100, mostly long-term drift, against which a real reversal is a rounding error.
 *
 * <h2>Nothing here looks forward</h2>
 *
 * All three points are in the past at the close of bar `t`, so the signal triggers on bar `t` and
 * the entry is bar `t`'s close. That is unusual for this codebase — the shapes it replaced needed
 * `P[t+1]` and had to be entered a bar late — and it is a real advantage of this definition rather
 * than an oversight: the stall is complete when the second flat point closes.
 *
 * <h2>Pure</h2>
 *
 * No ephemeris, no network, no chart. Values in, findings out.
 */

/** Price rows are [open, close, low, high] — NOT OHLC order. */
export const OPEN = 0;
export const CLOSE = 1;
export const LOW = 2;
export const HIGH = 3;

export const UP = 'up';
export const DOWN = 'down';

/** Bars in the rolling window that sets the local scale. */
export const RANGE_WINDOW = 30;

/** What counts as level, as a fraction of that range, and the range the slider offers. */
export const FLAT_TOLERANCE = 0.0075;
export const FLAT_TOLERANCE_MIN = 0.002;
export const FLAT_TOLERANCE_MAX = 0.015;

/** What counts as a thrust, on the same scale. */
export const MIN_SLOPE = 0.05;
export const MIN_SLOPE_MIN = 0.02;
export const MIN_SLOPE_MAX = 0.10;

/** The forward windows the summary scores entries over. */
export const FORWARD_HORIZONS = [3, 5, 10];

export const ENTRY_COLORS = { up: '#2e7d32', down: '#c62828' };

/**
 * The TrendPulse curve as one value per chart bar.
 *
 * <p>The overlay carries `[{date, value}]` on its own dates, and the detector needs an array indexed
 * the same way as the price bars — "the point before and the point before that" only means anything
 * if the points are the chart's own.
 *
 * <p>A bar the curve does not cover becomes `undefined` rather than being filled from its
 * neighbour. Carrying a value forward would manufacture a level pair, which is half of the shape
 * being looked for: the detector would find stalls in the gaps.
 */
export function alignSeriesToBars(series, dates) {
  const out = new Array(Array.isArray(dates) ? dates.length : 0);
  if (!Array.isArray(series) || !Array.isArray(dates)) return out;
  const byDate = new Map();
  series.forEach((pt) => {
    if (!pt) return;
    const d = String(pt.date || '').slice(0, 10);
    const v = Number(pt.value);
    if (d && Number.isFinite(v)) byDate.set(d, v);
  });
  dates.forEach((d, i) => {
    const v = byDate.get(String(d).slice(0, 10));
    if (v !== undefined) out[i] = v;
  });
  return out;
}

/**
 * The local scale at bar `t`: the rolling range and the two thresholds derived from it.
 *
 * <p>`TP[t-29..t]` — thirty values, which is what the definition says. Returns null before the
 * window is full, and also when the range is zero: with `R = 0` both thresholds collapse, `|Δ| <= 0`
 * is satisfied by any exactly flat stretch, and every bar of it would read as a stall.
 */
export function rollingScale(values, t, { window = RANGE_WINDOW, flatPct = FLAT_TOLERANCE,
  slopePct = MIN_SLOPE } = {}) {
  if (!Array.isArray(values) || t < window - 1) return null;
  let min = Infinity;
  let max = -Infinity;
  for (let i = t - window + 1; i <= t; i++) {
    const v = values[i];
    if (!Number.isFinite(v)) return null;             // a gap makes the window a smaller window
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const range = max - min;
  if (!(range > 0)) return null;
  return { min, max, range, flat: flatPct * range, slope: slopePct * range };
}

/**
 * Every entry trigger in the curve, in bar order.
 *
 * <p>Three conditions, all at bar `t`:
 * <ol>
 *   <li>the stall — `|P[t] - P[t-1]| <= ε_flat`</li>
 *   <li>the thrust — `|P[t-1] - P[t-2]| >= Δ_slope`</li>
 *   <li>a non-flat origin — `|P[t-1] - P[t-2]| > ε_flat`</li>
 * </ol>
 *
 * <p><b>The third is redundant at every setting the sliders allow</b>, and is implemented anyway.
 * `Δ_slope` bottoms out at 2% and `ε_flat` tops out at 1.5%, so anything clearing the thrust test
 * has already cleared this one — it can never reject a bar the second condition accepted. It earns
 * its place as the guard that keeps the two ranges from being widened into each other: if the
 * sliders ever overlap, this is the line that stops an extended flat consolidation being read as a
 * thrust into a stall, and its absence would not fail a test, it would silently change what the
 * study means.
 *
 * @param {number[]} values the TrendPulse curve, one value per chart bar
 */
export function detectEntries(values, opts = {}) {
  const out = [];
  if (!Array.isArray(values)) return out;
  for (let t = 2; t < values.length; t++) {
    const scale = rollingScale(values, t, opts);
    if (!scale) continue;
    const [before, mid, now] = [values[t - 2], values[t - 1], values[t]];
    if (![before, mid, now].every(Number.isFinite)) continue;

    const stall = Math.abs(now - mid);
    const incoming = mid - before;
    if (stall > scale.flat) continue;                       // 1. horizontal stall
    if (Math.abs(incoming) < scale.slope) continue;         // 2. strong incoming thrust
    if (Math.abs(incoming) <= scale.flat) continue;         // 3. non-flat origin — see above

    // The call is the opposite of the thrust that stalled: momentum arrived and stopped.
    const bullish = incoming <= -scale.slope;
    out.push({
      index: t,
      // Every point is in the past at this bar's close, so there is nothing to wait for. The
      // shapes this replaced needed P[t+1] and had to be entered a bar late.
      confirmedAt: t,
      direction: bullish ? UP : DOWN,
      variant: bullish ? 'BullishEntry' : 'BearishEntry',
      value: now,
      points: [t - 2, t - 1, t],
      stall: [t - 1, t],
      detail: `${bullish ? 'Downward' : 'Upward'} thrust of ${Math.abs(incoming).toFixed(2)} `
        + `(>= Δ=${scale.slope.toFixed(2)}) stalled within ${stall.toFixed(2)} `
        + `(<= ε=${scale.flat.toFixed(2)}) — ${bullish ? 'bullish' : 'bearish'} entry`,
    });
  }
  return out;
}

/* ─────────────────────────────────────── how it turned out ──────────────────────────────────── */

/**
 * The forward return an entry earned over N bars, signed BY ITS OWN DIRECTION.
 *
 * <p>A bearish entry profits from a fall, so its return is the fall. Without that sign the two
 * halves cancel and the summary reports roughly zero whatever the signal was worth.
 *
 * <p>Null when the horizon runs past the data — unresolved, not a loss. Counting it as one would
 * penalise the detector for firing recently, which is exactly when its output matters.
 */
export function forwardReturn(ohlc, entry, n) {
  if (!Array.isArray(ohlc) || !entry) return null;
  const start = Number.isInteger(entry.confirmedAt) ? entry.confirmedAt : entry.index;
  const end = start + n;
  if (end >= ohlc.length) return null;
  const from = Array.isArray(ohlc[start]) ? ohlc[start][CLOSE] : null;
  const to = Array.isArray(ohlc[end]) ? ohlc[end][CLOSE] : null;
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === 0) return null;
  const move = (to - from) / from;
  return entry.direction === DOWN ? -move : move;
}

/** Every entry with its 3-, 5- and 10-bar returns attached. */
export function scoreEntries(entries, ohlc, { horizons = FORWARD_HORIZONS } = {}) {
  return (Array.isArray(entries) ? entries : []).map((e) => {
    const returns = {};
    horizons.forEach((n) => { returns[n] = forwardReturn(ohlc, e, n); });
    return { ...e, returns };
  });
}

/**
 * Counts, and how the entries fared.
 *
 * <p>The counter is what the study asks for; the win rate is here because it is the only way to
 * know whether the counter is worth reading. A flat outcome counts against the signal — it claimed
 * a move, and nothing happening is a failed prediction.
 */
export function summariseEntries(scored, { horizons = FORWARD_HORIZONS } = {}) {
  const list = Array.isArray(scored) ? scored : [];
  const perHorizon = {};
  horizons.forEach((n) => {
    const vals = list.map(e => e.returns?.[n]).filter(Number.isFinite);
    const wins = vals.filter(v => v > 0);
    perHorizon[n] = {
      judged: vals.length,
      unresolved: list.length - vals.length,
      successRate: vals.length ? (100 * wins.length) / vals.length : null,
    };
  });
  return {
    total: list.length,
    bullish: list.filter(e => e.direction === UP).length,
    bearish: list.filter(e => e.direction === DOWN).length,
    perHorizon,
    horizons,
  };
}

/* ────────────────────────────────────────── the drawing ─────────────────────────────────────── */

export const ENTRY_OVERLAY_ID = 'trendpulse-entries';

/**
 * The overlay: arrows on the PRICE bars, and the stall highlighted on the TREND PULSE pane.
 *
 * <p>Both, because they answer different halves of the same question. The arrow is where the trade
 * is, on bar `t`, below the bar for a bullish entry and above it for a bearish one — the side the
 * call points away from, so the mark never covers the move it is about. The segment is the evidence,
 * drawn along the curve between `P[t-1]` and `P[t]`, which is the flat stretch that triggered it.
 *
 * <p>The segment goes on the indicator's own axis, so it lies exactly on the two points it names.
 * Drawn against price it would be a horizontal line at an unrelated level.
 */
export function buildEntryOverlay(scored, values, barData,
  { horizons = FORWARD_HORIZONS, offsetPx = 16 } = {}) {
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  const ohlc = barData && Array.isArray(barData.ohlc) ? barData.ohlc : [];
  const list = Array.isArray(scored) ? scored : [];

  /** The bar extreme this call points away from. */
  const anchor = (i, direction) => {
    const bar = ohlc[i];
    if (!Array.isArray(bar)) return null;
    const v = direction === DOWN ? bar[HIGH] : bar[LOW];
    return Number.isFinite(v) ? v : null;
  };

  const points = list
    .filter(e => dates[e.index] !== undefined && anchor(e.index, e.direction) !== null)
    .map((e) => {
      const bullish = e.direction === UP;
      return {
        date: dates[e.index],
        price: anchor(e.index, e.direction),
        position: bullish ? 'below' : 'above',
        symbolOffsetPx: offsetPx,
        symbol: 'triangle',
        symbolSize: [13, 13],
        // A bearish arrow points down; ECharts rotates about the symbol's own centre.
        ...(bullish ? {} : { symbolRotate: 180 }),
        color: bullish ? ENTRY_COLORS.up : ENTRY_COLORS.down,
        tooltipLines: [
          `${bullish ? 'Bullish' : 'Bearish'} entry — stall after a `
            + `${bullish ? 'down' : 'up'} thrust`,
          `  ${e.detail}`,
          // The curve's own numbers, since the arrow sits on price and cannot show them.
          `  TrendPulse ${e.points.map(i => (Number.isFinite(values?.[i])
            ? values[i].toFixed(2) : '?')).join(' → ')}`,
          ...horizons.map(n => `  ${n}-bar: ${e.returns?.[n] === null
            || e.returns?.[n] === undefined ? 'unresolved'
            : `${(e.returns[n] * 100).toFixed(2)}%`}`),
        ],
      };
    });

  const paneSegments = list
    .filter(e => dates[e.stall[0]] !== undefined && dates[e.stall[1]] !== undefined
      && Number.isFinite(values?.[e.stall[0]]) && Number.isFinite(values?.[e.stall[1]]))
    .map(e => ({
      date1: dates[e.stall[0]],
      value1: values[e.stall[0]],
      date2: dates[e.stall[1]],
      value2: values[e.stall[1]],
      color: e.direction === UP ? ENTRY_COLORS.up : ENTRY_COLORS.down,
    }));

  return {
    id: ENTRY_OVERLAY_ID,
    type: 'trendpulseentries',
    label: 'TrendPulse entries',
    points,
    paneSegments,
  };
}
